import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

const useAutosave = (data, onSave, { debounceMs = 2000, enabled = true } = {}) => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const dataRef = useRef(data);
  const onSaveRef = useRef(onSave);
  dataRef.current = data;
  onSaveRef.current = onSave;

  const dispatch = useDispatch();
  const isInitialMount = useRef(true);
  const timeoutRef = useRef(null);
  const lastSavedData = useRef(null);

  const saveData = useCallback(async () => {
    setStatus('saving');
    setError(null);

    try {
      await dispatch(onSaveRef.current(dataRef.current));
      lastSavedData.current = JSON.stringify(dataRef.current);
      setStatus('success');
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setStatus('idle'), 2000);
    } catch (e) {
      setError(e);
      setStatus('error');
    }
  }, [dispatch]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastSavedData.current = JSON.stringify(dataRef.current);
      return;
    }

    if (JSON.stringify(dataRef.current) === lastSavedData.current) {
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (Object.keys(dataRef.current).length) {
        saveData();
      }
    }, debounceMs);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [data, saveData, debounceMs, enabled]);

  return { status, error };
};

export default useAutosave;